import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';
import { Input } from '@shadcn/components/ui/input';
import { Label } from '@shadcn/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/ui/select';
import { format } from 'date-fns';
import { Calendar } from '@shadcn/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@shadcn/components/ui/popover';
import { Separator } from '@shadcn/components/ui/separator';
import { 
  X, 
  Filter, 
  RotateCcw,
  Calendar as CalendarIcon
} from 'lucide-react';

import { StatusBadge } from '../TicketSystem/StatusBadge';
import { TicketTypeIcon } from '../TicketSystem/TicketTypeIcon';
import { 
  TICKET_TYPES, 
  META_STATUS,
  TICKET_TYPE_LABELS, 
  META_STATUS_LABELS 
} from '../../constants';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shadcn/components/ui/dialog';


const formatDateForInput = (date) => {
  if (!date) return '';
  return date.toISOString().split('T')[0];
};


const TicketFilters = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  onClose, 
  open = true 
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);


  useEffect(() => {
    setLocalFilters(filters);
    
    if (filters.dateFrom) {
      setDateFrom(new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      setDateTo(new Date(filters.dateTo));
    }
  }, [filters]);


  const handleLocalFilterChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };


  const handleDateFromChange = (date) => {
    setDateFrom(date);
    handleLocalFilterChange('dateFrom', date ? format(date, 'yyyy-MM-dd') : '');
  };

  const handleDateToChange = (date) => {
    setDateTo(date);
    handleLocalFilterChange('dateTo', date ? format(date, 'yyyy-MM-dd') : '');
  };


  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    onClose();
  };


  const handleResetFilters = () => {
    setLocalFilters({});
    setDateFrom(null);
    setDateTo(null);
    onClearFilters();
  };


  const hasChanges = JSON.stringify(localFilters) !== JSON.stringify(filters);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl z-[1500] overflow-y-auto max-h-[80vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-600" />
              <DialogTitle >Advanced Filters</DialogTitle>
            </div>
          </div>
        </DialogHeader>
        <CardDescription>
          Filter tickets by various criteria
        </CardDescription>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Ticket Type</Label>
              <Select
                value={localFilters.type || 'ALL_TYPES'}
                onValueChange={(value) => handleLocalFilterChange('type', value === 'ALL_TYPES' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent className="z-[1600]">
                  <SelectItem value="ALL_TYPES">All types</SelectItem>
                  {Object.values(TICKET_TYPES).map(type => (
                    <SelectItem key={type} value={type}>
                      <div className="flex items-center gap-2">
                        <TicketTypeIcon type={type} size="xs" showBackground={false} />
                        {TICKET_TYPE_LABELS[type]}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Meta Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={localFilters.metaStatus || 'ALL_STATUSES'}
                onValueChange={(value) => handleLocalFilterChange('metaStatus', value === 'ALL_STATUSES' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent className="z-[1600]">
                  <SelectItem value="ALL_STATUSES">All statuses</SelectItem>
                  {Object.values(META_STATUS).map(status => (
                    <SelectItem key={status} value={status}>
                      <div className="flex items-center gap-2">
                        <StatusBadge 
                          metaStatus={status} 
                          showOriginal={false}
                        />
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Date Range */}
          <div className="space-y-4">
          <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="openOnly"
                checked={localFilters.open || false}
                onChange={(e) => handleLocalFilterChange('open', e.target.checked)}
                className="h-4 w-4 border-gray-300 rounded"
              />
              <Label htmlFor="openOnly" className="mb-0 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Show Open Tickets Only
              </Label>
            </div>
          
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* From Date */}
              <div className="space-y-2">
                <Label>From Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom ? dateFrom.toLocaleDateString() : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[1600]" align="start">
                    <Calendar
                      mode="single"
                      selected={dateFrom}
                      onSelect={handleDateFromChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* To Date */}
              <div className="space-y-2">
                <Label>To Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateTo ? dateTo.toLocaleDateString() : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[1600]" align="start">
                    <Calendar
                      mode="single"
                      selected={dateTo}
                      onSelect={handleDateToChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <Separator />

          {/* Text Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Customer Name */}
            <div className="space-y-2">
              <Label>Customer Name</Label>
              <Input
                placeholder="Search by customer name"
                value={localFilters.customerName || ''}
                onChange={(e) => handleLocalFilterChange('customerName', e.target.value)}
              />
            </div>

            {/* Store Name */}
            <div className="space-y-2">
              <Label>Store Name</Label>
              <Input
                placeholder="Search by store name"
                value={localFilters.storeName || ''}
                onChange={(e) => handleLocalFilterChange('storeName', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Order Number */}
            <div className="space-y-2">
              <Label>Order Number</Label>
              <Input
                placeholder="Search by order number"
                value={localFilters.orderNumber || ''}
                onChange={(e) => handleLocalFilterChange('orderNumber', e.target.value)}
              />
            </div>

            {/* Staff Name */}
            <div className="space-y-2">
              <Label>Staff Name</Label>
              <Input
                placeholder="Search by staff name"
                value={localFilters.staffName || ''}
                onChange={(e) => handleLocalFilterChange('staffName', e.target.value)}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleResetFilters}
              className="flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset All
            </Button>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              
              <Button
                onClick={handleApplyFilters}
                disabled={!hasChanges}
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </DialogContent>
    </Dialog>
  );
};

export default TicketFilters;
